import apiClient from '@/lib/apiClient';
import { ApiResponse, EmailTemplateCategory } from '@/types';

export interface BankAccount {
  label: string;
  accountName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  upiId: string;
  razorpayLink: string;
}

// Per-department Mandrill "From" identity (label = department/purpose).
export interface EmailSender {
  label: string;
  email: string;
  fromName: string;
}

export interface CompanySettingsData {
  bank_account_name?: string;
  bank_account_number?: string;
  bank_ifsc_code?: string;
  bank_name?: string;
  bank_upi_id?: string;
  razorpay_link?: string;
  bank_accounts?: string;
  email_senders?: string;
  // The two employee designation dropdowns, each a JSON-encoded array of
  // labels: `designation_prefixes` is the LEVEL list, `designation_roles` the
  // DEPARTMENT list. Encoded strings rather than arrays because
  // company_settings is a key/value table of strings and every other
  // list-valued setting is stored the same way.
  designation_prefixes?: string;
  designation_roles?: string;
  billing_email?: string;
  service_email_mfbox?: string;
  service_email_website?: string;
  service_email_app?: string;
  company_legal_name?: string;
  company_address?: string;
  gst_percentage?: string;
  quotation_validity_days?: string;
  financial_year_start_month?: string;
  financial_year_start_day?: string;
  // Auto-purge retention windows in days; '0' or blank means keep forever / 90 default.
  audit_retention_days?: string;
  session_retention_days?: string;
  // OTP delivery channel(s): 'sms' | 'terminal' | 'both'. 'terminal'/'both' print
  // the raw OTP to the server log and only apply outside production (production is
  // always capped to SMS).
  otp_delivery_mode?: string;
  // Initial number of days for the payment installment due-date span (ADR-035).
  // Stored as a digit string; default is 30 when blank.
  payment_installment_days?: string;
  // Days before an installment's due date to send the payment reminder email
  // (ADR-037). Digit string; default 2 when blank. 0 = only the on-due reminder.
  installment_reminder_days_before?: string;
  // Renewal email-ladder schedule (digit strings, 0-365). Notice and the two
  // follow-ups fire BEFORE a renewal's due date, the service termination AFTER
  // it. Defaults when blank: 60 / 30 / 15 / 1.
  renewal_notice_days_before?: string;
  renewal_followup1_days_before?: string;
  renewal_followup2_days_before?: string;
  renewal_termination_days_after?: string;
  // 'true'/'false'. When on (default), the public T&C page only accepts a link
  // that was actually emailed to the client (blocks RM self-acceptance). Turn
  // off in dev to test acceptance without sending.
  tc_enforce_sent_before_accept?: string;
  // Operational alerts to the tech/support team about the system itself, not about a
  // client or a lead (ADR-045). Enabled unless explicitly 'false'; with no recipients
  // nothing is sent, so a blank address field is the off switch.
  ops_alert_enabled?: string;
  /** Comma / semicolon separated. Parsed, validated and capped server-side on read. */
  ops_alert_emails?: string;
  /** Minimum gap between two alerts about the same condition. Digit string; default 6. */
  ops_alert_cooldown_hours?: string;
  // Record-keeping CC: addresses every outgoing email of that category is copied
  // to. Merged in SERVER-SIDE at send time, so the composer shows them as locked
  // chips and the sender cannot remove them. Comma / semicolon separated.
  record_cc_sales?: string;
  record_cc_billing?: string;
  record_cc_renewal?: string;
  record_cc_service?: string;
  record_cc_internal?: string;
}

/** Parsed record-keeping CC addresses, keyed by email template category. */
export type RecordKeepingCc = Record<EmailTemplateCategory, string[]>;

/**
 * The admin-configured employee designation vocabulary.
 *
 * `prefixes` is the LEVEL list (Senior, Mid-Level, …); `roles` is the
 * DEPARTMENT list (Sales, Billing, Renewal, Service, Admin). Both are stored in
 * company_settings so an admin can extend them without a deploy, which is why
 * this is fetched rather than declared as a union type here.
 */
export interface DesignationConfig {
  prefixes: string[];
  roles: string[];
}

/**
 * Used only when the request itself fails or returns nothing.
 *
 * Mirrors the server's DESIGNATION_DEFAULTS (server/src/constants/index.ts).
 * A hand-maintained copy, like LEAD_SOURCE_OPTIONS: there is no shared package
 * between the workspaces. It exists so a network blip does not leave the
 * onboarding form with two empty dropdowns and no way to submit - the server
 * validates whatever is chosen against the real list anyway, so a stale copy
 * here produces a clean 422, never a bad write.
 */
const DESIGNATION_FALLBACK: DesignationConfig = {
  prefixes: ['Senior', 'Mid-Level', 'Junior'],
  roles: ['Sales', 'Billing', 'Renewal', 'Service', 'Admin'],
};

// Numeric, range-clamped financial-year start returned by the non-admin
// /company-settings/financial-year endpoint. Drives the dashboard FYTD filter.
export interface FinancialYearConfig {
  startMonth: number;
  startDay: number;
}

// One selectable company bank account (label + bank name only) exposed to
// non-admins for the email bank selector (ADR-020).
export interface BankAccountOption {
  label: string;
  bankName: string;
}

// Sender label + email for the RM's From-address dropdown.
export interface EmailSenderOption {
  label: string;
  email: string;
}

// Single source of the default FY start (1 April), shared by the service
// fallback and the dashboard filter so they can't drift.
export const FY_DEFAULT: FinancialYearConfig = { startMonth: 4, startDay: 1 };

export const companySettingsService = {
  getAll: async (): Promise<CompanySettingsData> => {
    const { data } =
      await apiClient.get<ApiResponse<CompanySettingsData>>('/admin/company-settings');
    return data.data ?? {};
  },

  update: async (settings: CompanySettingsData): Promise<CompanySettingsData> => {
    const { data } = await apiClient.put<ApiResponse<CompanySettingsData>>(
      '/admin/company-settings',
      settings,
    );
    return data.data ?? {};
  },

  getFinancialYear: async (): Promise<FinancialYearConfig> => {
    const { data } = await apiClient.get<ApiResponse<FinancialYearConfig>>(
      '/company-settings/financial-year',
    );
    return data.data ?? FY_DEFAULT;
  },

  // The two employee designation dropdowns (level + department). Non-admin: an
  // employee needs them mid-onboarding, long before they could have admin rights.
  getDesignations: async (): Promise<DesignationConfig> => {
    // The catch is the whole point of DESIGNATION_FALLBACK, and `?? FALLBACK`
    // alone did not deliver it: that only covers a request that SUCCEEDS with an
    // empty body. A rejected one (offline, 500, a proxy hiccup) left
    // `useDesignations().data` undefined, so the onboarding form rendered a
    // Designation level select holding nothing but its disabled placeholder -
    // while the field was required and the control was enabled, because
    // `isLoading` was already false. The employee could not submit onboarding at
    // all, and onboarding gates the entire app.
    //
    // Resolving instead of rethrowing is deliberate: the server validates the
    // chosen value against the live list on save, so a stale local list can only
    // ever produce a clean 422, never a bad write.
    try {
      const { data } = await apiClient.get<ApiResponse<DesignationConfig>>(
        '/company-settings/designations',
      );
      return data.data ?? DESIGNATION_FALLBACK;
    } catch {
      return DESIGNATION_FALLBACK;
    }
  },

  getBankAccounts: async (): Promise<BankAccountOption[]> => {
    const { data } = await apiClient.get<ApiResponse<BankAccountOption[]>>(
      '/company-settings/bank-accounts',
    );
    return data.data ?? [];
  },

  getEmailSenders: async (): Promise<EmailSenderOption[]> => {
    const { data } = await apiClient.get<ApiResponse<EmailSenderOption[]>>(
      '/company-settings/email-senders',
    );
    return data.data ?? [];
  },

  getPaymentInstallmentDays: async (): Promise<number> => {
    const { data } = await apiClient.get<ApiResponse<{ days: number }>>(
      '/company-settings/payment-installment-days',
    );
    return data.data?.days ?? 30;
  },

  // The record-keeping CC per category, so a composer can show the locked chips.
  // Read-only and already the addresses the server merges in at send time, so
  // what the chips show cannot disagree with what actually goes out. An empty
  // map on failure: the chips are a display of an enforcement that happens
  // elsewhere, so losing them must never block a send.
  getRecordKeepingCc: async (): Promise<RecordKeepingCc> => {
    const empty: RecordKeepingCc = {
      sales: [],
      billing: [],
      renewal: [],
      service: [],
      internal: [],
    };
    try {
      const { data } = await apiClient.get<ApiResponse<RecordKeepingCc>>(
        '/company-settings/record-cc',
      );
      return data.data ?? empty;
    } catch {
      return empty;
    }
  },
};
